import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Maximize2, Minimize2 } from 'lucide-react';
import { getScheduleItems, timeToMinutes } from '../firebase/dailyScheduleService';
import { getClasses, getLessons, getUnits } from '../firebase/lessonPlannerService';
import { getAssignments } from '../firebase/assignmentService';
import './BigScreenView.css';

// Utility function to convert 24-hour time to 12-hour AM/PM format
const formatTime12Hour = (time24) => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

function BigScreenView() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [scheduleItems, setScheduleItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [units, setUnits] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [currentItem, setCurrentItem] = useState(null);
  const [activeTimers, setActiveTimers] = useState([]);
  const [todaysLessons, setTodaysLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Manual Timer/Stopwatch State
  const [timerMode, setTimerMode] = useState('stopwatch');
  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [countdownMinutes, setCountdownMinutes] = useState(5);
  const [showTimerPanel, setShowTimerPanel] = useState(false);
  const [timerPosition, setTimerPosition] = useState({ x: window.innerWidth - 400, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Manual timer update
  useEffect(() => {
    if (!timerRunning) return;

    const interval = setInterval(() => {
      setElapsedSeconds(prev => {
        if (timerMode === 'countdown') {
          const newValue = prev - 1;
          if (newValue <= 0) {
            setTimerRunning(false);
            return 0;
          }
          return newValue;
        } else {
          return prev + 1;
        }
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timerRunning, timerMode]);

  // Load data
  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentUser, navigate]);

  const loadData = async () => {
    try {
      console.log('BigScreenView: Loading data...');
      setLoading(true);
      const [scheduleResult, classesResult, lessonsResult, unitsResult, assignmentsResult] = await Promise.all([
        getScheduleItems(currentUser.uid),
        getClasses(currentUser.uid),
        getLessons(currentUser.uid),
        getUnits(currentUser.uid),
        getAssignments(currentUser.uid)
      ]);

      console.log('BigScreenView: Data loaded', {
        scheduleItems: scheduleResult.items?.length || 0,
        classes: classesResult.classes?.length || 0,
        lessons: lessonsResult.lessons?.length || 0,
        units: unitsResult.units?.length || 0,
        assignments: assignmentsResult.assignments?.length || 0
      });

      if (scheduleResult.success) {
        setScheduleItems(scheduleResult.items);
      }
      if (classesResult.success) {
        setClasses(classesResult.classes);
      }
      if (lessonsResult.success) {
        setLessons(lessonsResult.lessons);
      }
      if (unitsResult.success) {
        setUnits(unitsResult.units);
      }
      if (assignmentsResult.success) {
        setAssignments(assignmentsResult.assignments);
      }
      setLoading(false);
      console.log('BigScreenView: Loading complete');
    } catch (error) {
      console.error('BigScreenView: Error loading data:', error);
      setLoading(false);
    }
  };

  // Determine current class/timer
  useEffect(() => {
    const now = currentTime;
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][now.getDay()];

    const todaysItems = scheduleItems.filter(item =>
      item.daysOfWeek && item.daysOfWeek.includes(dayOfWeek)
    );

    let current = null;
    const timers = [];

    todaysItems.forEach(item => {
      const startMinutes = timeToMinutes(item.startTime);
      const endMinutes = timeToMinutes(item.endTime);

      if (item.type === 'class' && currentMinutes >= startMinutes && currentMinutes < endMinutes) {
        current = item;
      }

      if (item.type === 'timer' && currentMinutes >= startMinutes && currentMinutes < endMinutes) {
        const remainingMinutes = endMinutes - currentMinutes;
        const isExpiring = remainingMinutes <= 2;
        timers.push({ ...item, remainingMinutes, isExpiring });
      }
    });

    // Add class countdown timers
    todaysItems.forEach(item => {
      if (item.type === 'class') {
        const startMinutes = timeToMinutes(item.startTime);
        const endMinutes = timeToMinutes(item.endTime);

        // Tardy countdown (before class starts)
        if (item.tardyCountdownEnabled && item.tardyCountdownMinutes) {
          const tardyTime = startMinutes - item.tardyCountdownMinutes;
          const diff = currentMinutes - tardyTime;
          if (diff >= 0 && diff < 1) {
            timers.push({
              id: `${item.id}-tardy`,
              type: 'countdown',
              timerType: 'tardy',
              timerName: `Tardy Warning - ${getClassName(item.classId)}`,
              className: getClassName(item.classId),
              isExpiring: true
            });
          }
        }

        // Bell countdown (before class ends)
        if (item.bellCountdownEnabled && item.bellCountdownMinutes) {
          const bellTime = endMinutes - item.bellCountdownMinutes;
          const diff = currentMinutes - bellTime;
          if (diff >= 0 && diff < 1) {
            timers.push({
              id: `${item.id}-bell`,
              type: 'countdown',
              timerType: 'bell',
              timerName: `Bell Warning - ${getClassName(item.classId)}`,
              className: getClassName(item.classId),
              isExpiring: true
            });
          }
        }
      }
    });

    setCurrentItem(current);
    setActiveTimers(timers);
  }, [currentTime, scheduleItems, classes]);

  // Helper function to parse YYYY-MM-DD string as local date (not UTC)
  const parseDateLocal = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Get today's lessons
  useEffect(() => {
    if (!currentItem || !lessons.length) {
      setTodaysLessons([]);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];

    const todayLessons = lessons.filter(l => {
      const lessonDate = l.startDate || l.scheduledDate;
      if (!lessonDate) return false;

      const dateStr = typeof lessonDate === 'string'
        ? lessonDate
        : lessonDate.toDate
        ? lessonDate.toDate().toISOString().split('T')[0]
        : new Date(lessonDate).toISOString().split('T')[0];

      if (dateStr !== todayStr) return false;

      if (!l.unitIds || l.unitIds.length === 0) return false;

      const lessonUnits = units.filter(u => l.unitIds.includes(u.id));
      const belongsToClass = lessonUnits.some(unit =>
        unit.classIds && unit.classIds.includes(currentItem.classId)
      );

      return belongsToClass;
    });

    setTodaysLessons(todayLessons);
  }, [currentItem, lessons, units]);

  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls?.name || 'Unknown Class';
  };

  const getUnitTitle = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit?.title || 'No Unit';
  };

  const getClassAssignments = () => {
    if (!currentItem) return [];
    const today = new Date().toISOString().split('T')[0];
    return assignments.filter(a =>
      a.classId === currentItem.classId &&
      a.dueDate &&
      a.dueDate >= today
    ).sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  };

  const formatTimerDisplay = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimerStart = () => {
    if (timerMode === 'countdown' && elapsedSeconds === 0) {
      setElapsedSeconds(countdownMinutes * 60);
    }
    setTimerRunning(true);
  };

  const handleTimerStop = () => {
    setTimerRunning(false);
  };

  const handleTimerReset = () => {
    setTimerRunning(false);
    setElapsedSeconds(0);
  };

  const handleTimerMouseDown = (e) => {
    if (e.target.closest('.timer-close-btn') || e.target.closest('.timer-controls')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - timerPosition.x,
      y: e.clientY - timerPosition.y
    });
  };

  const handleTimerMouseMove = (e) => {
    if (!isDragging) return;
    setTimerPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    });
  };

  const handleTimerMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleTimerMouseMove);
      document.addEventListener('mouseup', handleTimerMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleTimerMouseMove);
        document.removeEventListener('mouseup', handleTimerMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  console.log('BigScreenView: Rendering', { loading, currentItem, todaysLessons: todaysLessons.length });

  if (loading) {
    return (
      <div className="big-screen-loading">
        <div className="loading-spinner"></div>
        <p>Loading classroom display...</p>
      </div>
    );
  }

  return (
    <div className="big-screen-view">
      <div className="big-screen-header">
        <div className="header-left">
          {currentItem && (
            <div className="current-class-info-big">
              <div className="class-name-big">{getClassName(currentItem.classId)}</div>
              {currentItem.period && <div className="period-badge-big">{currentItem.period}</div>}
            </div>
          )}
          {currentItem && (
            <div className="class-time-big">
              {formatTime12Hour(currentItem.startTime)} - {formatTime12Hour(currentItem.endTime)}
            </div>
          )}
        </div>

        <div className="header-center">
          <div className="current-time-big">
            {currentTime.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            })}
          </div>
        </div>

        <div className="header-right">
          <button onClick={() => setShowTimerPanel(true)} className="control-btn-big">
            ⏱️ Timer
          </button>
          <button onClick={toggleFullscreen} className="control-btn-big">
            {isFullscreen ? <Minimize2 size={24} /> : <Maximize2 size={24} />}
          </button>
          <button onClick={() => navigate(-1)} className="control-btn-big">
            <ArrowLeft size={24} />
            <span>Exit</span>
          </button>
        </div>
      </div>

      {/* Timer Warnings */}
      {activeTimers.length > 0 && (
        <div className="timer-warnings-big">
          {activeTimers.map(timer => (
            <div
              key={timer.id}
              className={`timer-warning-big ${timer.timerType || ''} ${timer.type === 'countdown' ? 'countdown' : ''}`}
            >
              <div className="timer-icon-big">
                {timer.timerType === 'tardy' ? '⚠️' : timer.timerType === 'bell' ? '🔔' : '⏰'}
              </div>
              <div className="timer-message-big">
                <h2>{timer.timerName || timer.timerType === 'tardy' ? 'Tardy Warning' : timer.timerType === 'bell' ? 'Bell Warning' : 'Timer Alert'}</h2>
                <p>{timer.className || (timer.remainingMinutes !== undefined ? `${timer.remainingMinutes} minute${timer.remainingMinutes !== 1 ? 's' : ''} remaining` : '')}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Main Content */}
      {currentItem ? (
        <div className="big-screen-content">
          {/* Today's Lessons */}
          {todaysLessons.length > 0 && (
            <div className="lesson-card-big">
              <h3>📚 Today's Lesson{todaysLessons.length > 1 ? 's' : ''}</h3>
              {todaysLessons.map((lesson, idx) => (
                <div key={lesson.id}>
                  <h4>{lesson.title}</h4>
                  {lesson.unitId && (
                    <div className="lesson-unit-badge-big">{getUnitTitle(lesson.unitId)}</div>
                  )}
                  {lesson.learningObjective && (
                    <div className="lesson-section-big">
                      <strong>Learning Objective</strong>
                      <p>{lesson.learningObjective}</p>
                    </div>
                  )}
                  {lesson.lessonSummary && (
                    <div className="lesson-section-big">
                      <strong>Summary</strong>
                      <p>{lesson.lessonSummary}</p>
                    </div>
                  )}
                  {idx < todaysLessons.length - 1 && <hr style={{ margin: '1rem 0', opacity: 0.3 }} />}
                </div>
              ))}
            </div>
          )}

          <div className="sidebar-big">
            {/* Assignments */}
            <div className="assignments-card-big">
              <h3>📝 Assignments</h3>
              {getClassAssignments().length > 0 ? (
                <ul className="assignments-list-big">
                  {getClassAssignments().slice(0, 5).map(assignment => (
                    <li key={assignment.id} className={assignment.dueDate === new Date().toISOString().split('T')[0] ? 'due-today' : ''}>
                      <strong>{assignment.title}</strong>
                      <span className="assignment-due-big">
                        Due: {new Date(assignment.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="no-data-big">No upcoming assignments</p>
              )}
            </div>

            {/* Resources */}
            {(() => {
              const allResources = todaysLessons.flatMap(lesson => lesson.resources || []);
              return allResources.length > 0 && (
                <div className="resources-card-big">
                  <h3>🔗 Resources</h3>
                  <div className="resources-list-big">
                    {allResources.map((resource, index) => (
                      <a
                        key={index}
                      href={resource.fileUrl || resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="resource-link-big"
                    >
                      <span className="resource-icon-big">📎</span>
                      <span>{resource.name || resource.fileName || `Resource ${index + 1}`}</span>
                    </a>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      ) : (
        <div className="no-class-section-big">
          <div className="no-class-message-big">
            <h2>🎉</h2>
            <p>No class in session</p>
          </div>
        </div>
      )}

      {/* Manual Timer Widget */}
      {showTimerPanel && (
        <div
          className="manual-timer-widget-big"
          style={{
            left: `${timerPosition.x}px`,
            top: `${timerPosition.y}px`,
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
        >
          <div className="timer-panel-big" onMouseDown={handleTimerMouseDown}>
            <div className="timer-panel-header-big">
              <div className="timer-mode-switch-big">
                <button
                  className={timerMode === 'stopwatch' ? 'active' : ''}
                  onClick={() => { setTimerMode('stopwatch'); handleTimerReset(); }}
                >
                  Stopwatch
                </button>
                <button
                  className={timerMode === 'countdown' ? 'active' : ''}
                  onClick={() => { setTimerMode('countdown'); handleTimerReset(); }}
                >
                  Countdown
                </button>
              </div>
              <button onClick={() => setShowTimerPanel(false)} className="timer-close-btn-big">
                ✕
              </button>
            </div>

            {timerMode === 'countdown' && !timerRunning && elapsedSeconds === 0 && (
              <div className="countdown-setup-big">
                <label>Minutes:</label>
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={countdownMinutes}
                  onChange={(e) => setCountdownMinutes(parseInt(e.target.value) || 1)}
                />
              </div>
            )}

            <div className="timer-display-big">
              {formatTimerDisplay(elapsedSeconds)}
            </div>

            <div className="timer-controls-big">
              {!timerRunning ? (
                <button onClick={handleTimerStart} className="timer-btn-big start-btn-big">
                  Start
                </button>
              ) : (
                <button onClick={handleTimerStop} className="timer-btn-big stop-btn-big">
                  Pause
                </button>
              )}
              <button onClick={handleTimerReset} className="timer-btn-big reset-btn-big">
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BigScreenView;
